import { SAVE_CONFIG } from '../constants';
import { DEMO_SLOT_INDEX } from '../DemoSave';
import type { SaveData } from '../GameState';

export interface SlotSaveData {
  name: string;
  timestamp: number;
  saveData: SaveData;
}

export type LegacySlotSaveData = SaveData & { name?: string };

type StoredSlotData = SlotSaveData | LegacySlotSaveData;

export interface ParsedSlotData {
  name: string | null;
  timestamp: number;
  saveData: SaveData;
}

export interface SaveOverview {
  hasAnySave: boolean;
  latestSaveName: string | null;
  latestSaveTimestamp: number | null;
}

export const MANUAL_SAVE_SLOT_COUNT = 5;

export const isSaveData = (value: unknown): value is SaveData => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<SaveData>;

  return (
    typeof candidate.version === 'string' &&
    typeof candidate.timestamp === 'number' &&
    typeof candidate.playerPos === 'object' &&
    candidate.playerPos !== null &&
    typeof candidate.gameMap === 'object' &&
    candidate.gameMap !== null &&
    typeof candidate.buildingLayer === 'object' &&
    candidate.buildingLayer !== null
  );
};

export const parseStoredSlotData = (raw: string): ParsedSlotData => {
  const parsed = JSON.parse(raw) as StoredSlotData;

  if (parsed && typeof parsed === 'object' && 'saveData' in parsed) {
    const slot = parsed as SlotSaveData;
    if (isSaveData(slot.saveData)) {
      return {
        name: typeof slot.name === 'string' ? slot.name : null,
        timestamp:
          typeof slot.timestamp === 'number'
            ? slot.timestamp
            : slot.saveData.timestamp,
        saveData: slot.saveData,
      };
    }
  }

  if (isSaveData(parsed)) {
    const legacy = parsed as LegacySlotSaveData;
    return {
      name: typeof legacy.name === 'string' ? legacy.name : null,
      timestamp:
        typeof legacy.timestamp === 'number' ? legacy.timestamp : Date.now(),
      saveData: legacy,
    };
  }

  throw new Error('Invalid slot save data');
};

export const serializeSlotData = (name: string, saveData: SaveData): string => {
  const slotData: SlotSaveData = {
    name,
    timestamp: saveData.timestamp ?? Date.now(),
    saveData,
  };

  return JSON.stringify(slotData);
};

const considerSave = (
  currentBest: { name: string | null; timestamp: number | null },
  candidate: { name: string | null; timestamp: number | null }
): { name: string | null; timestamp: number | null } => {
  if (
    candidate.timestamp === null ||
    !Number.isFinite(candidate.timestamp) ||
    candidate.timestamp === undefined
  ) {
    return currentBest;
  }

  if (
    currentBest.timestamp === null ||
    (typeof currentBest.timestamp === 'number' &&
      candidate.timestamp > currentBest.timestamp)
  ) {
    return candidate;
  }

  return currentBest;
};

export const getSaveOverview = (): SaveOverview => {
  if (typeof window === 'undefined') {
    return {
      hasAnySave: false,
      latestSaveName: null,
      latestSaveTimestamp: null,
    };
  }

  let hasAnySave = false;
  let latest = {
    name: null as string | null,
    timestamp: null as number | null,
  };

  try {
    const autoRaw = window.localStorage.getItem(SAVE_CONFIG.SAVE_KEY);
    if (autoRaw) {
      const parsedAuto = JSON.parse(autoRaw);
      if (isSaveData(parsedAuto)) {
        hasAnySave = true;
        latest = considerSave(latest, {
          name: 'Auto Save',
          timestamp: parsedAuto.timestamp ?? null,
        });
      }
    }
  } catch {
    // Ignore malformed auto save data
  }

  for (let slotIndex = 0; slotIndex < MANUAL_SAVE_SLOT_COUNT; slotIndex++) {
    if (slotIndex === DEMO_SLOT_INDEX) {
      continue;
    }

    try {
      const saveKey = `${SAVE_CONFIG.SAVE_KEY}_slot_${slotIndex}`;
      const raw = window.localStorage.getItem(saveKey);
      if (!raw) {
        continue;
      }

      const parsedSlot = parseStoredSlotData(raw);
      hasAnySave = true;
      latest = considerSave(latest, {
        name:
          parsedSlot.name && parsedSlot.name.trim().length > 0
            ? parsedSlot.name
            : `Save ${slotIndex + 1}`,
        timestamp: parsedSlot.timestamp ?? null,
      });
    } catch {
      // Ignore malformed save slots
    }
  }

  return {
    hasAnySave,
    latestSaveName: latest.name,
    latestSaveTimestamp: latest.timestamp,
  };
};
